import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../api';

const TodaySales = () => {
  const [allSales, setAllSales] = useState([]);
  const [allPurchases, setAllPurchases] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingSales, setLoadingSales] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showRates, setShowRates] = useState(false);
  const [visibleItemRateIndices, setVisibleItemRateIndices] = useState(new Set());

  const toggleItemRate = (idx) => {
    setVisibleItemRateIndices(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };
  const navigate = useNavigate();

  const [isDeletingSale, setIsDeletingSale] = useState(false);

  const handleDeleteSale = async (sale) => {
    if (!sale) return;
    const targetId = sale.id || sale._id || sale.docId || sale.vchNo;
    if (!targetId) {
      alert("Error: Sale ID not found.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete invoice ${sale.vchNo ? '#' + sale.vchNo : ''} (${sale.custName || 'Walk-in'}) for ₹${(sale.netAmount || sale.amount || 0)}? This cannot be undone.`)) {
      return;
    }

    try {
      setIsDeletingSale(true);
      const res = await fetch(apiUrl(`/api/sales/${targetId}`), { method: 'DELETE' });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || errJson.details || 'Failed to delete sale');
      }

      setAllSales(prev => prev.filter(s => {
        const sId = String(s.id || s._id || s.docId || s.vchNo || '');
        return sId !== String(targetId);
      }));
      setSelectedSale(null);
      await fetchTodaySales();
    } catch (err) {
      console.error('Delete sale error:', err);
      alert('Error deleting bill: ' + err.message);
    } finally {
      setIsDeletingSale(false);
    }
  };

  const fetchTodaySales = async () => {
    try {
      setLoadingSales(true);
      const [res, purchasesRes] = await Promise.all([
        fetch(apiUrl('/api/sales')),
        fetch(apiUrl('/api/purchases')).catch(() => null)
      ]);
      if (res.ok) {
        const data = await res.json();
        setAllSales(data);
      }
      if (purchasesRes && purchasesRes.ok) {
        const pData = await purchasesRes.json();
        setAllPurchases(pData);
      }
    } catch (error) {
      console.error('Failed to fetch sales', error);
    } finally {
      setLoadingSales(false);
    }
  };

  useEffect(() => {
    fetchTodaySales();
  }, []);

  const purchaseCostMap = React.useMemo(() => {
    const map = {};
    if (allPurchases && Array.isArray(allPurchases)) {
      allPurchases.forEach(p => {
        if (p.items && Array.isArray(p.items)) {
          p.items.forEach(pItem => {
            if (pItem.productName) {
              const key = pItem.productName.trim().toUpperCase();
              const c = parseFloat(pItem.costRate || pItem.costPrice || pItem.rate) || 0;
              if (c > 0) map[key] = c;
            }
          });
        }
      });
    }
    return map;
  }, [allPurchases]);

  const todaySalesData = allSales.filter(sale => sale.date === selectedDate);
  const totalRevenue = todaySalesData.reduce((acc, sale) => acc + (parseFloat(sale.netAmount || sale.amount || 0) || 0), 0);
  const totalBills = todaySalesData.length;

  const totalCost = todaySalesData.reduce((sum, sale) => {
    let cost = 0;
    if (sale.items && Array.isArray(sale.items)) {
      sale.items.forEach(item => {
        const key = (item.productName || '').trim().toUpperCase();
        const pCost = purchaseCostMap[key] || parseFloat(item.costPrice || item.costRate || item.purchaseRate) || 0;
        cost += (parseFloat(item.qty) || 0) * pCost;
      });
    }
    return sum + cost;
  }, 0);

  const totalProfit = totalRevenue - totalCost;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
      {/* Large Pop-up Modal Container */}
      <div className="bg-white w-full max-w-5xl h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white px-6 py-4 flex items-center justify-between shrink-0 shadow-md">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white shadow-inner">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl md:text-2xl font-black tracking-tight text-white">Live Sales Register</h2>
                <span className="flex items-center gap-1 bg-emerald-400/30 border border-emerald-300/40 text-white text-[11px] font-black px-2.5 py-0.5 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-emerald-300 animate-ping"></span>
                  LIVE
                </span>
              </div>
              <p className="text-xs text-emerald-100 font-medium mt-0.5">Real-time daily transaction history</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-xl border border-white/20">
              <span className="text-xs font-bold text-emerald-100 hidden sm:inline">Select Date:</span>
              <input 
                type="date" 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)} 
                className="bg-white text-slate-800 font-extrabold text-xs px-2.5 py-1 rounded-lg outline-none shadow-sm cursor-pointer" 
              />
            </div>
            
            <button 
              onClick={() => navigate('/dashboard')}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-lg transition-colors"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Top Summary Bar inside Modal */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-3.5 flex flex-wrap items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-[10px] uppercase font-black tracking-widest text-slate-400 block">Total Revenue</span>
              <span className="text-xl font-black text-emerald-600">₹{totalRevenue.toFixed(2)}</span>
            </div>
            <div className="h-8 w-px bg-slate-200"></div>
            <div>
              <span className="text-[10px] uppercase font-black tracking-widest text-slate-400 block">Total Bills</span>
              <span className="text-xl font-black text-slate-800">{totalBills}</span>
            </div>
            <div className="h-8 w-px bg-slate-200"></div>
            <div>
              <span className="text-[10px] uppercase font-black tracking-widest text-emerald-700 block">Today's Profit (आजचा नफा)</span>
              <span className="text-xl font-black text-emerald-700">₹{totalProfit.toFixed(2)}</span>
            </div>
          </div>

          <button 
            onClick={fetchTodaySales} 
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
            Refresh
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-100/60">
          {loadingSales ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mb-3"></div>
              <p className="text-xs font-bold text-slate-500">Loading today's sales data...</p>
            </div>
          ) : todaySalesData.length > 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="p-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-black text-slate-700 uppercase tracking-wider">
                  Sales Transactions ({todaySalesData.length})
                </span>
                <span className="text-[11px] text-slate-400 font-bold">Click any row to inspect invoice details</span>
              </div>
              <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                <thead>
                  <tr className="bg-slate-100/70 text-slate-500 text-[10px] uppercase tracking-wider font-extrabold border-b border-slate-200">
                    <th className="p-3">Customer Name</th>
                    <th className="p-3">Mobile No</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Products Purchased</th>
                    <th className="p-3 text-right">Total Bill (₹)</th>
                    <th className="p-3 text-right">Received / Paid (₹)</th>
                    <th className="p-3 text-right">Balance Due (₹)</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {todaySalesData.map((sale, index) => {
                    const netVal = parseFloat(sale.netAmount || sale.amount || 0) || 0;
                    let paidVal = parseFloat(sale.amountPaid !== undefined ? sale.amountPaid : (sale.amountReceived !== undefined ? sale.amountReceived : 0));
                    if (paidVal === 0) {
                      if (sale.saleType === 'CASH' || sale.saleType === 'ONLINE 1' || sale.saleType === 'ONLINE 2' || sale.saleType === 'ONLINE') {
                        paidVal = netVal;
                      } else if (sale.saleType === 'MIX') {
                        paidVal = (parseFloat(sale.mixCashAmount) || 0) + (parseFloat(sale.mixOnlineAmount) || 0);
                      }
                    }
                    const effectivePaid = Math.min(paidVal, netVal);
                    const balanceDueVal = sale.balanceDue !== undefined ? parseFloat(sale.balanceDue) : Math.max(0, netVal - effectivePaid);

                    return (
                      <tr 
                        key={index} 
                        className="hover:bg-emerald-50/60 transition-colors cursor-pointer group" 
                        onClick={() => setSelectedSale(sale)}
                      >
                        <td className="p-3">
                          <div className="font-extrabold text-slate-800 text-sm group-hover:text-emerald-700">
                            {sale.custName || 'Walk-in Customer'}
                          </div>
                          {sale.vchNo && <div className="text-[10px] text-slate-400 font-semibold">Bill #{sale.vchNo}</div>}
                        </td>
                        <td className="p-3 font-bold text-slate-600">
                          {sale.custMob || 'N/A'}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${sale.saleType === 'CREDIT' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                            {sale.saleType || 'CASH'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-0.5">
                            {sale.items?.map((item, i) => (
                              <div key={i} className="text-[11px] font-medium text-slate-600">
                                <span className="text-emerald-600 font-bold">{item.qty}x</span> {item.productName}
                              </div>
                            ))}
                            {!sale.items && <span className="text-slate-400 text-[10px]">No items</span>}
                          </div>
                        </td>
                        <td className="p-3 text-right font-black text-sm text-slate-900">
                          ₹{netVal.toFixed(2)}
                        </td>
                        <td className="p-3 text-right font-black text-xs text-emerald-700">
                          ₹{effectivePaid.toFixed(2)}
                        </td>
                        <td className="p-3 text-right">
                          <span className={`px-2 py-0.5 rounded-lg text-xs font-black border ${balanceDueVal > 0 ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                            {balanceDueVal > 0 ? `₹${balanceDueVal.toFixed(2)} Pending` : `₹0.00 (Paid)`}
                          </span>
                        </td>
                        <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSale(sale);
                            }}
                            disabled={isDeletingSale}
                            className="px-2.5 py-1 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white rounded-lg font-bold text-xs border border-rose-200 transition-all inline-flex items-center gap-1 cursor-pointer shadow-2xs"
                            title="Delete this sale bill"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200/80 shadow-sm text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4M8 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-base font-extrabold text-slate-700">No Sales Recorded Today Yet</h3>
              <p className="text-xs text-slate-400 mt-1 max-w-sm">No transactions found for {selectedDate}. Use the date selector above to view previous sales records.</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-white border-t border-slate-200 flex items-center justify-between shrink-0">
          <div className="text-xs font-bold text-slate-500">
            Showing transactions for <span className="text-slate-800 font-extrabold">{selectedDate}</span>
          </div>
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-bold text-xs shadow-sm transition-colors"
          >
            Close
          </button>
        </div>

      </div>

      {/* Sale Details Nested Modal */}
      {selectedSale && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-white shrink-0">
              <h3 className="text-lg font-black text-emerald-900 flex items-center gap-2">
                <div className="w-2 h-6 rounded-full bg-emerald-400"></div>
                Invoice Details {selectedSale.vchNo && `#${selectedSale.vchNo}`}
              </h3>
              <button onClick={() => setSelectedSale(null)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors">
                ✕
              </button>
            </div>
            
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Customer</p>
                  <p className="font-black text-slate-700 text-sm">{selectedSale.custName || 'Walk-in'}</p>
                  <p className="text-xs font-bold text-slate-500">{selectedSale.custMob || 'No Mobile'}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Bill Type</p>
                  <p className="font-black text-slate-700 text-sm">{selectedSale.saleType || 'CASH'}</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-100 shadow-sm shadow-emerald-100">
                  <p className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-widest mb-1">Paid Amount</p>
                  <p className="font-black text-emerald-700 text-lg">₹{(selectedSale.saleType === 'CREDIT' ? 0 : (selectedSale.netAmount || selectedSale.amount || 0)).toFixed(2)}</p>
                </div>
                <div className="bg-rose-50 p-3 rounded-xl border border-rose-100 shadow-sm shadow-rose-100">
                  <p className="text-[10px] font-bold text-rose-600/70 uppercase tracking-widest mb-1">Pending Due</p>
                  <p className="font-black text-rose-700 text-lg">₹{(selectedSale.saleType === 'CREDIT' ? (selectedSale.netAmount || selectedSale.amount || 0) : 0).toFixed(2)}</p>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-black text-slate-700 uppercase tracking-widest mb-2 flex items-center gap-2">
                  Products List
                  <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[9px]">{selectedSale.items?.length || 0}</span>
                </h4>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
                        <th className="p-3 border-b border-slate-200">Product Name</th>
                        <th className="p-3 border-b border-slate-200">Company</th>
                        <th className="p-3 border-b border-slate-200 text-center">Qty</th>
                        <th className="p-3 border-b border-slate-200 text-right">MRP</th>
                        <th className="p-3 border-b border-slate-200 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span>Rate</span>
                            <button 
                              type="button" 
                              onClick={() => setShowRates(!showRates)}
                              title={showRates ? "Hide Rates" : "View Rates"}
                              className="p-0.5 text-slate-400 hover:text-teal-600 rounded transition-colors"
                            >
                              {showRates ? (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.018 10.018 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a9.978 9.978 0 01-2.13 3.592M3 3l18 18"/></svg>
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="p-3 border-b border-slate-200 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedSale.items?.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="p-3 text-xs font-black text-slate-700">{item.productName}</td>
                          <td className="p-3 text-[10px] font-bold text-slate-500">{item.company || '-'}</td>
                          <td className="p-3 text-xs font-black text-slate-700 text-center bg-slate-50/50">{item.qty}</td>
                          <td className="p-3 text-xs font-bold text-slate-500 text-right">{parseFloat(item.mrp || 0).toFixed(2)}</td>
                          <td className="p-3 text-xs font-bold text-slate-600 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span>{showRates || visibleItemRateIndices.has(idx) ? `${parseFloat(item.rate || 0).toFixed(2)}` : '••••'}</span>
                              <button 
                                type="button" 
                                onClick={() => toggleItemRate(idx)}
                                className="p-0.5 text-slate-400 hover:text-teal-600 transition-colors"
                                title={showRates || visibleItemRateIndices.has(idx) ? "Hide Rate" : "View Rate"}
                              >
                                {showRates || visibleItemRateIndices.has(idx) ? (
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.018 10.018 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a9.978 9.978 0 01-2.13 3.592M3 3l18 18"/></svg>
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="p-3 text-sm font-black text-emerald-600 text-right">{parseFloat(item.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                      {!selectedSale.items?.length && (
                        <tr><td colSpan="6" className="p-4 text-center text-xs text-slate-400 font-bold">No products found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-3 shrink-0">
               <div className="text-xs font-bold text-slate-500 flex items-center gap-3">
                 <span>Gross: <span className="text-slate-700">₹{parseFloat(selectedSale.grossAmount || 0).toFixed(2)}</span></span>
                 <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                 <span>Discount: <span className="text-slate-700">₹{parseFloat(selectedSale.discount || 0).toFixed(2)}</span></span>
               </div>
               <div className="flex items-center gap-3">
                 <button
                   type="button"
                   onClick={() => handleDeleteSale(selectedSale)}
                   disabled={isDeletingSale}
                   className="px-3 py-1.5 bg-rose-50 hover:bg-rose-600 hover:text-white text-rose-600 rounded-xl font-bold text-xs border border-rose-200 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                   title="Delete this sale bill"
                 >
                   <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                   <span>{isDeletingSale ? 'Deleting...' : 'Delete Bill'}</span>
                 </button>
                 <div className="text-sm font-black text-slate-800">
                   Net Total: <span className="text-emerald-600 text-xl ml-1">₹{(selectedSale.netAmount || selectedSale.amount || 0).toFixed(2)}</span>
                 </div>
               </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodaySales;
